import { useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { AuthHeading } from "@/components/tuw/auth/AuthHeading"
import BrandLockup from "@/components/tuw/auth/BrandLockup"
import { PasswordField } from "@/components/tuw/auth/fields"
import {
  afterConfirmed, AlreadyConfirmed, InvitationOutcome, PostExpired, WrongAccount,
} from "@/components/tuw/openHouse/WaitingScreen"
import { Button } from "@/components/ui/button"
import { confirmByLink } from "@/lib/openHouse/guest"
import { CONFIRMATION as C, CONFIRMATION_REFUSALS, POST_EXPIRED_REASONS } from "@/lib/openHouse/wording"

// Key turn's link page (spec 6f-b2; guest-wording §4 "Confirmation link
// page"), served by OpenHouse::ConfirmationsController#show at
// /post-project/confirm. The confirmation email's button carries its token in
// the URL fragment, which no server or link scanner sees; the layout's head
// script hands it over and clears it from the address, and the page posts it
// with the account's password. The password lives only in this page's state
// and the request body. Confirmed, it goes to the posted project, or first
// shows what became of an invited freelancer's invitation (InvitationOutcome).
//
// `links` is the server's { projects, waiting }. A post past its deadline
// (`link_expired`, `expired`) or deleted by Sundown (`post_expired`) ends in
// guest-wording §7's lines.

// Refusals that end the form, by the server's reason.
const ENDS = {
  link_invalid: "invalid", already_confirmed: "already", wrong_account: "wrongAccount",
  ...Object.fromEntries(POST_EXPIRED_REASONS.map((reason) => [reason, "expired"])),
}

// Refusals shown above the form.
const ALERTS = { password_locked: C.linkPage.tooManyAttempts, ...CONFIRMATION_REFUSALS }

// The fragment's token, taken once from the layout's head script
// (shared/_fragment_handoff), which cleared it from the address before any
// analytics tag loaded. The enrollment's link page takes it the same way.
export function takeToken() {
  const take = window.__tuwFragment
  return typeof take === "function" ? take() : ""
}

export default function OpenHouseConfirm({ links }) {
  const token = useRef(null)
  const [view, setView] = useState(null)
  const [password, setPassword] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState(null)
  const [alert, setAlert] = useState(null)
  const [outcome, setOutcome] = useState(null) // confirmed with an invitation: { invitation, redirect }

  useEffect(() => {
    if (token.current === null) token.current = takeToken()
    setView(token.current ? "form" : "invalid")
  }, [])

  const submit = async (event) => {
    event.preventDefault()
    if (submitting || !password) return

    setSubmitting(true)
    setFieldError(null)
    setAlert(null)
    try {
      afterConfirmed(await confirmByLink(token.current, password), (shown) => {
        setOutcome(shown)
        setView("outcome")
      })
    } catch (err) {
      setSubmitting(false)
      if (ENDS[err.reason]) return setView(ENDS[err.reason])
      if (err.reason === "password_incorrect") return setFieldError(C.linkPage.passwordIncorrect)

      setAlert(ALERTS[err.reason] ?? C.failed)
    }
  }

  let body = null
  if (view === "outcome") {
    body = <InvitationOutcome invitation={outcome.invitation} projectHref={outcome.redirect} />
  } else if (view === "expired") {
    body = <PostExpired oldConfirmation />
  } else if (view === "invalid") {
    body = (
      <>
        <AuthAlert tone="bad">{C.link.invalid}</AuthAlert>
        <Button asChild variant="outline">
          <a href={links.waiting}>{C.return}</a>
        </Button>
      </>
    )
  } else if (view === "already") {
    // The account itself, signed in, is sent on to the project, so whoever
    // reads this is not signed in here (new-flow-wording §27).
    body = <AlreadyConfirmed href={links.projects} body={C.alreadyConfirmed.bodyNotSignedIn} />
  } else if (view === "wrongAccount") {
    body = <WrongAccount />
  } else if (view === "form") {
    body = (
      <form onSubmit={submit} noValidate className="flex flex-col gap-[22px]">
        <AuthHeading title={C.linkPage.heading}>{C.linkPage.body}</AuthHeading>
        {alert && <AuthAlert tone="bad">{alert}</AuthAlert>}
        <PasswordField id="oh-confirm-password" label={C.linkPage.passwordLabel} autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)} error={fieldError} />
        <Button type="submit" disabled={submitting || !password} className="gap-1.5">
          {submitting && <Loader2 className="size-4 animate-spin" />}
          {submitting ? C.submitting : C.submit}
        </Button>
      </form>
    )
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card px-8 py-3.5 max-[561px]:px-4">
        <BrandLockup className="h-[26px]" />
      </header>
      <main className="flex justify-center px-4 py-12">
        <div className="flex w-full max-w-[400px] flex-col gap-[22px]">{body}</div>
      </main>
    </div>
  )
}
